import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Phone, MapPin, Building2,
  Plus, ChevronDown, ChevronUp, ChevronRight, X, Copy, Check,
  PhoneCall, PhoneOff, Edit2, AlertTriangle,
  User, Clock, Loader2, Mail, Star,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Drawer } from '../ui/Drawer.js';
import { Button } from '../ui/Button.js';
import { SkeletonCard } from '../ui/Skeleton.js';
import { useActivityLog } from '../../hooks/useActivityLog.js';
import { useToast } from '../ui/Toast.js';
import { useAuth } from '../../lib/auth.js';
import { landlordsApi, activityApi, tagsApi, agentsApi, assignmentsApi, unitsApi } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';

// Sherzod 2026-05-06: deep-link callback. When the user clicks a
// unit_no inside the drawer, the page-level handler closes the drawer
// and scrolls/highlights the matching row in the main table by
// data-unit-id.
interface LandlordDrawerProps {
  landlordId: number | null;
  onClose: () => void;
  onJumpToUnit?: (unitId: number) => void;
}

// ── WhatsApp SVG icon ────────────────────────────────────────────────────────

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// ── Stage config ──────────────────────────────────────────────────────────────

const STAGE_OPTIONS = [
  { value: 'new',          label: 'New' },
  { value: 'sale',         label: 'Sale' },
  { value: 'sold',         label: 'Sold' },
  { value: 'rent',         label: 'Rent' },
  { value: 'rented',       label: 'Rented' },
  { value: 'potential',    label: 'Potential' },
  { value: 'unavailable',  label: 'Unavailable' },
  { value: 'no_sale',      label: 'No Sale' },
  { value: 'no_answer',    label: 'No Answer' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'agent',        label: 'Agent' },
] as const;

type StageValue = (typeof STAGE_OPTIONS)[number]['value'];

// Sherzod-specified palette 2026-05-03 (matches web/components/ui/Badge.tsx):
const STAGE_PILL: Record<string, string> = {
  new:          'bg-blue-100 text-blue-800 border border-blue-200',
  sale:         'bg-green-100 text-green-800 border border-green-200',
  sold:         'bg-red-100 text-red-800 border border-red-200',
  rent:         'bg-orange-100 text-orange-800 border border-orange-200',
  rented:       'bg-rose-200 text-rose-900 border border-rose-300',
  potential:    'bg-purple-100 text-purple-800 border border-purple-200',
  unavailable:  'bg-gray-100 text-gray-700 border border-gray-200',
  no_sale:      'bg-red-100 text-red-800 border border-red-200',
  no_answer:    'bg-yellow-100 text-yellow-800 border border-yellow-200',
  wrong_number: 'bg-red-100 text-red-800 border border-red-200',
  agent:        'bg-sky-100 text-sky-800 border border-sky-200',
};

const STAGE_DOT: Record<string, string> = {
  new:          'bg-blue-500',
  sale:         'bg-green-500',
  sold:         'bg-red-500',
  rent:         'bg-orange-500',
  rented:       'bg-rose-700',
  potential:    'bg-purple-500',
  unavailable:  'bg-gray-400',
  no_sale:      'bg-red-500',
  no_answer:    'bg-yellow-500',
  wrong_number: 'bg-red-700',
  agent:        'bg-sky-500',
};

function stageClasses(stage: string): string {
  return STAGE_PILL[stage] ?? 'bg-hover-bg text-secondary border border-border';
}

function stageDotClass(stage: string): string {
  return STAGE_DOT[stage] ?? 'bg-secondary';
}

function stageLabel(stage: string): string {
  return STAGE_OPTIONS.find((o) => o.value === stage)?.label ?? stage;
}

// ── Activity helpers ──────────────────────────────────────────────────────────

const ACTIVITY_CONFIG: Record<string, { label: string; dot: string }> = {
  call:         { label: 'Call',          dot: 'bg-success' },
  whatsapp:     { label: 'WhatsApp',      dot: 'bg-whatsapp' },
  note:         { label: 'Note',          dot: 'bg-primary' },
  stage_change: { label: 'Stage changed', dot: 'bg-yellow-400' },
  assignment:   { label: 'Assigned',      dot: 'bg-purple-400' },
  edit:         { label: 'Edited',        dot: 'bg-secondary' },
  import:       { label: 'Imported',      dot: 'bg-gray-300' },
};

// Drawer-scoped: returns the action phrase that follows the user name
// to form a full sentence. Used only by the Activity Timeline below.
// Sherzod 2026-05-03: timeline reads as "{User} {action} · {time ago}".
// When ev.unitNo is set, the phrase frames the action against that unit
// so lead-level events like "moved to Sale" become "moved unit X to Sale".
function activityActionPhrase(ev: {
  type: string;
  detail: Record<string, unknown> | null;
  unitNo?: string | null;
}): string {
  const detail = (ev.detail ?? {}) as Record<string, unknown>;
  // Backend writes detail.api_type (snake_case); some older paths used apiType.
  // Read both so neither shape silently falls through.
  const apiType =
    typeof detail.api_type === 'string' ? detail.api_type
    : typeof detail.apiType === 'string' ? detail.apiType
    : null;
  const action = typeof detail.action === 'string' ? detail.action : null;
  const unitRef = ev.unitNo ? `unit ${ev.unitNo}` : null;
  const target = unitRef ?? 'the lead';

  // Calls
  if (ev.type === 'call') {
    if (apiType === 'call_answered') return 'logged a call — answered';
    if (apiType === 'call_no_answer') return 'logged a call — no answer';
    if (apiType === 'call_made') return 'made a call';
    return 'logged a call';
  }
  if (ev.type === 'no_answer') return 'tried to call (no answer)';
  // WhatsApp
  if (ev.type === 'whatsapp') {
    if (apiType === 'whatsapp_replied') return 'received a WhatsApp reply';
    return 'sent a WhatsApp message';
  }
  // Notes
  if (ev.type === 'note') return 'added a note';
  // Per-unit comment (Slice C of Backlog 08)
  if (ev.type === 'comment') {
    return unitRef ? `commented on ${unitRef}` : 'added a comment';
  }
  // Stage moves
  if (ev.type === 'stage_change') {
    const from = detail.from ? stageLabel(String(detail.from)) : null;
    const to = detail.to ? stageLabel(String(detail.to)) : null;
    if (from && to) return `moved ${target} from ${from} to ${to}`;
    if (to) return `moved ${target} to ${to}`;
    return `changed the status of ${target}`;
  }
  // Per-unit assignee change (Slice C)
  if (ev.type === 'assignee_change') {
    const to = typeof detail.to === 'number' || typeof detail.to === 'string'
      ? String(detail.to)
      : null;
    return to
      ? `reassigned ${target} (agent #${to})`
      : `cleared the assignee on ${target}`;
  }
  // Per-unit follow-up (Slice C)
  if (ev.type === 'followup') {
    const date = typeof detail.date === 'string' ? detail.date : null;
    return date
      ? `set follow-up on ${target} to ${date}`
      : `cleared the follow-up on ${target}`;
  }
  // Per-unit tag change (Slice C)
  if (ev.type === 'tag_change') {
    const tagName = typeof detail.tag_name === 'string' ? detail.tag_name : null;
    const verb = action === 'remove' ? 'removed' : 'added';
    if (tagName) return `${verb} tag ${tagName} on ${target}`;
    return `${verb} a tag on ${target}`;
  }
  // Phone add/remove on the LEAD (Sherzod 2026-05-03: phones belong to lead)
  if (ev.type === 'phone_added') {
    const phone = typeof detail.phone === 'string' ? detail.phone : null;
    const fromUnit = typeof detail.from_unit_no === 'string' ? detail.from_unit_no : null;
    const breadcrumb = fromUnit ? ` (from unit ${fromUnit})` : '';
    if (action === 'removed') {
      return phone ? `removed phone ${phone}` : 'removed a phone';
    }
    if (phone) return `added phone ${phone}${breadcrumb}`;
    return `added a phone${breadcrumb}`;
  }
  // Per-unit field edits (beds, view, unit_no, notes) — Slice C
  if (ev.type === 'unit_update') {
    const after = (detail.after ?? {}) as Record<string, unknown>;
    const fields = Object.keys(after);
    if (fields.length === 0) return `updated ${target}`;
    return `updated ${fields.join(', ')} on ${target}`;
  }
  // Reminders — Slice D
  if (ev.type === 'reminder') return unitRef ? `set a reminder on ${unitRef}` : 'set a reminder';
  if (ev.type === 'reminder_done') return unitRef ? `completed a reminder on ${unitRef}` : 'completed a reminder';
  // Lead-level assignment
  if (ev.type === 'assignment') {
    const to = typeof detail.agent_name === 'string' ? detail.agent_name
      : typeof detail.assignee_name === 'string' ? detail.assignee_name
      : null;
    return to ? `assigned this lead to ${to}` : 'assigned this lead';
  }
  // Edits — sub-action variants (tags, star, generic field edits)
  if (ev.type === 'edit') {
    if (action === 'tag_added') {
      const tag = typeof detail.tag_name === 'string' ? detail.tag_name : null;
      return tag ? `added the tag ${tag}` : 'added a tag';
    }
    if (action === 'tag_removed') {
      const tag = typeof detail.tag_name === 'string' ? detail.tag_name : null;
      return tag ? `removed the tag ${tag}` : 'removed a tag';
    }
    if (action === 'star' || detail.to === true && detail.field === 'isStarred') return 'starred the lead';
    if (action === 'unstar' || detail.to === false && detail.field === 'isStarred') return 'unstarred the lead';
    if (action === 'unit_edit') return 'edited unit details';
    return 'edited the lead details';
  }
  if (ev.type === 'import') return 'imported this lead';
  // Fallback — verb form of the type
  return `did ${ev.type}`;
}

function activityDetail(ev: {
  type: string;
  detail: Record<string, unknown> | null;
  apiType?: string | null;
}): { label: string; note?: string } {
  const cfg = ACTIVITY_CONFIG[ev.type] ?? { label: ev.type, dot: 'bg-gray-300' };
  let label = cfg.label;
  if (ev.apiType === 'call_answered') label = 'Call answered';
  if (ev.apiType === 'call_no_answer') label = 'No answer';
  if (ev.apiType === 'call_made') label = 'Call made';
  if (ev.apiType === 'whatsapp_sent') label = 'WhatsApp sent';
  if (ev.apiType === 'whatsapp_replied') label = 'WhatsApp replied';
  if (ev.apiType === 'note_added') label = 'Note';

  const note =
    ev.detail && typeof ev.detail.note === 'string' ? ev.detail.note : undefined;

  if (ev.type === 'stage_change' && ev.detail?.to) {
    label = `Stage → ${stageLabel(String(ev.detail.to))}`;
  }

  return { label, note };
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded hover:bg-hover-bg text-secondary hover:text-primary-text transition-colors"
      aria-label={`Copy ${text}`}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

// ── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] font-bold text-primary uppercase tracking-wider">
      {children}
    </span>
  );
}

// ── Per-unit expanded card body (Slice C) ────────────────────────────────────

interface UnitCardBodyProps {
  unitId: number;
  unitNo: string | null;
  landlordId: number;
  portfolioQueryKey: unknown[];
  isManager: boolean;
  initialStage: string;
}

function UnitCardBody({ unitId, unitNo, landlordId, portfolioQueryKey, isManager, initialStage }: UnitCardBodyProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [noteText, setNoteText] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phonePrimary, setPhonePrimary] = useState(false);
  const [tagName, setTagName] = useState('');
  const [followupDate, setFollowupDate] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  // Optimistic: tags added this session before a page refresh clears them
  const [optimisticTags, setOptimisticTags] = useState<Array<{ id: number; name: string }>>([]);
  // Stage: currentStage = the committed value (from server). previewStage = what the dropdown shows.
  // SAVE button is only enabled when they differ.
  const [currentStage, setCurrentStage] = useState<string>(initialStage);
  const [previewStage, setPreviewStage] = useState<string>(initialStage);
  const [unitAssignee, setUnitAssignee] = useState<{ id: number; name: string } | null>(null);
  const [unitFollowupSaved, setUnitFollowupSaved] = useState<string | null>(null);

  const commentsKey = ['unit-comments', unitId];
  const phonesKey = ['unit-phones', unitId];

  const { data: phones } = useQuery({
    queryKey: phonesKey,
    queryFn: () => unitsApi.getPhones(unitId),
    staleTime: 30_000,
  });

  const { data: comments } = useQuery({
    queryKey: commentsKey,
    queryFn: () => unitsApi.getComments(unitId),
    staleTime: 30_000,
  });

  const { data: availableTags } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
    staleTime: 120_000,
  });

  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agentsApi.list(),
    enabled: isManager && showAssign,
  });
  const agents = agentsData?.items ?? [];

  const patchUnit = useMutation({
    mutationFn: (body: import('@imza/shared').PatchUnitExtended) => unitsApi.patch(unitId, body),
    onSuccess: (data, vars) => {
      if (vars.stage) {
        // Commit: update currentStage (canonical saved value) and invalidate queries
        setCurrentStage(vars.stage);
        setPreviewStage(vars.stage);
        queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
        queryClient.invalidateQueries({ queryKey: ['units'] });
        toast('Status saved', 'success');
      } else if (vars.followupDate !== undefined) {
        queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
        setUnitFollowupSaved(data.followupDate);
        toast('Follow-up date saved', 'success');
      } else if (vars.assigneeId !== undefined) {
        queryClient.invalidateQueries({ queryKey: portfolioQueryKey });
        toast('Unit assignee updated', 'success');
      }
    },
    onError: (err: Error, vars) => {
      // On stage save error: rollback preview to the last committed value
      if (vars.stage) {
        setPreviewStage(currentStage);
      }
      toast(err.message, 'error');
    },
  });

  const addComment = useMutation({
    mutationFn: (body: string) => unitsApi.addComment(unitId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentsKey });
      // Real-time sync: refresh the main table so the Comment column
      // reflects the new latest_comment for this unit (Sherzod 2026-05-06).
      queryClient.invalidateQueries({ queryKey: ['units'] });
      setNoteText('');
      toast('Comment saved', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const addTag = useMutation({
    mutationFn: (tag: { id: number; name: string }) =>
      unitsApi.addTag(unitId, tag.id).then(() => tag),
    onSuccess: (tag) => {
      setOptimisticTags((prev) => [...prev.filter((t) => t.id !== tag.id), tag]);
      setTagName('');
      toast('Tag added', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const removeTag = useMutation({
    mutationFn: (tagId: number) => unitsApi.removeTag(unitId, tagId),
    onSuccess: (_data, tagId) => {
      setOptimisticTags((prev) => prev.filter((t) => t.id !== tagId));
      toast('Tag removed', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const createAndAddTag = useMutation({
    mutationFn: async (name: string) => {
      const created = await tagsApi.create(name);
      await unitsApi.addTag(unitId, created.id);
      return created;
    },
    onSuccess: (tag) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setOptimisticTags((prev) => [...prev.filter((t) => t.id !== tag.id), { id: tag.id, name: tag.name }]);
      setTagName('');
      toast('Tag created', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  // Sherzod 2026-05-06 (reversed from 2026-05-03): phone goes to the
  // UNIT, not the lead. The per-card phone input is now unit-scoped
  // again. Lead-level phones still added separately at the top of
  // the drawer. unitNo arg unused (kept for prop compat).
  void unitNo;
  const addPhone = useMutation({
    mutationFn: ({ phone, isPrimary }: { phone: string; isPrimary: boolean }) =>
      unitsApi.addPhone(unitId, phone, isPrimary),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit-phones', unitId] });
      queryClient.invalidateQueries({ queryKey: ['landlord-portfolio', landlordId] });
      // Real-time sync: patch the units list cache so the main table
      // row's PHONE cell updates without a refetch (Fix 3 Sherzod 2026-05-06).
      queryClient.invalidateQueries({ queryKey: ['units'] });
      setPhoneInput('');
      setPhonePrimary(false);
      toast('Phone added to unit', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const displayComments = showAllComments ? (comments ?? []) : (comments ?? []).slice(0, 3);

  function handleTagSubmit() {
    const trimmed = tagName.trim();
    if (!trimmed) return;
    const existing = availableTags?.items.find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) {
      addTag.mutate(existing);
    } else {
      createAndAddTag.mutate(trimmed);
    }
  }

  // 2-column grid layout (Sherzod 2026-05-03):
  // Rows 1-2 paired (Status|Follow-up, Assignee|Tags), Rows 3-4 full-width (Phone, Comment).
  // Label above each control, compact h-8 inputs, no per-field sub-card chrome.
  const labelCls = 'block text-[10px] uppercase font-semibold text-secondary tracking-wide mb-1';
  const inputCls = 'w-full h-8 px-2 bg-white border border-border rounded-md text-xs focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary';
  const btnCls = 'h-8 px-3 text-[11px] flex-shrink-0';

  return (
    <div className="space-y-2">

      {/* Row 1: Status + Follow-up */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Status</label>
          <div className="flex items-center gap-1.5">
            <select
              value={previewStage}
              onChange={(e) => setPreviewStage(e.target.value)}
              disabled={patchUnit.isPending}
              className={cn(inputCls, 'disabled:opacity-60')}
              aria-label="Unit status"
            >
              {STAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <span className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0',
              stageClasses(previewStage)
            )}>
              <span className={cn('w-1 h-1 rounded-full', stageDotClass(previewStage))} />
              {stageLabel(previewStage)}
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => patchUnit.mutate({ stage: previewStage as import('@imza/shared').LandlordStage })}
            loading={patchUnit.isPending}
            disabled={previewStage === currentStage || patchUnit.isPending}
            className={cn(btnCls, 'mt-1 w-full')}
          >
            Save
          </Button>
        </div>

        <div>
          <label className={labelCls}>Follow-up</label>
          <div className="flex gap-1.5">
            <input
              type="date"
              value={followupDate}
              onChange={(e) => setFollowupDate(e.target.value)}
              className={inputCls}
              aria-label="Follow-up date"
            />
            <Button
              size="sm"
              onClick={() => {
                patchUnit.mutate({ followupDate });
                setFollowupDate('');
              }}
              loading={patchUnit.isPending}
              disabled={!followupDate}
              className={btnCls}
            >
              Save
            </Button>
          </div>
          {unitFollowupSaved && (
            <p className="text-[10px] text-primary font-medium mt-0.5">
              Set: {unitFollowupSaved}
            </p>
          )}
        </div>
      </div>

      {/* Row 2: Assignee (manager-only) + Tags. Tags spans full width if !isManager. */}
      <div className={cn('grid gap-3', isManager ? 'grid-cols-2' : 'grid-cols-1')}>
        {isManager && (
          <div>
            <label className={labelCls}>Assignee</label>
            <div className="relative">
              <button
                onClick={() => setShowAssign((v) => !v)}
                className="w-full h-8 px-2 bg-white border border-border rounded-md text-xs text-primary-text flex items-center justify-between gap-1 hover:border-primary transition-colors"
              >
                <span className="flex items-center gap-1.5 truncate">
                  <User className="w-3 h-3 text-secondary flex-shrink-0" />
                  {unitAssignee ? unitAssignee.name : 'Unassigned'}
                </span>
                <ChevronDown className="w-3 h-3 text-secondary flex-shrink-0" />
              </button>
              {showAssign && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-48 overflow-y-auto py-1">
                  {agents.length > 0 ? agents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => {
                        patchUnit.mutate({ assigneeId: agent.id });
                        setUnitAssignee(agent);
                        setShowAssign(false);
                      }}
                      disabled={agent.id === unitAssignee?.id}
                      className={cn(
                        'w-full text-left px-2.5 py-1.5 text-xs transition-colors flex items-center gap-2',
                        agent.id === unitAssignee?.id
                          ? 'text-primary bg-primary/5 font-medium'
                          : 'text-primary-text hover:bg-hover-bg'
                      )}
                    >
                      <User className="w-3 h-3 text-secondary" />
                      {agent.name}
                      {agent.id === unitAssignee?.id && (
                        <Check className="w-3 h-3 text-primary ml-auto" />
                      )}
                    </button>
                  )) : (
                    <p className="px-2.5 py-1.5 text-xs text-secondary">No agents available</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className={labelCls}>Tags</label>
          <div className="flex gap-1.5">
            <input
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Tag name..."
              className={inputCls}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && tagName.trim()) handleTagSubmit();
              }}
            />
            <Button
              size="sm"
              onClick={handleTagSubmit}
              loading={addTag.isPending || createAndAddTag.isPending}
              disabled={!tagName.trim()}
              className={btnCls}
            >
              Add
            </Button>
          </div>
          {optimisticTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {optimisticTags.map((tag) => (
                <span key={tag.id} className="inline-flex items-center gap-0.5 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                  {tag.name}
                  <button
                    onClick={() => removeTag.mutate(tag.id)}
                    disabled={removeTag.isPending}
                    className="text-primary/60 hover:text-primary"
                    aria-label={`Remove tag ${tag.name}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {availableTags && availableTags.items.length > 0 && tagName.trim() === '' && (
            <div className="flex flex-wrap gap-1 mt-1">
              {availableTags.items
                .filter((t) => !optimisticTags.some((ot) => ot.id === t.id))
                .slice(0, 6)
                .map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => addTag.mutate(tag)}
                    disabled={addTag.isPending}
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-hover-bg border border-border text-secondary hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
                  >
                    {tag.name}
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Phone — adds to THIS UNIT's phone list (Sherzod 2026-05-06). */}
      <div>
        <label className={labelCls}>Phone</label>
        <div className="flex items-center gap-1.5">
          <input
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="+971 50 000 0000"
            type="tel"
            className={inputCls}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && phoneInput.trim()) {
                addPhone.mutate({ phone: phoneInput.trim(), isPrimary: phonePrimary });
              }
            }}
          />
          <Button
            size="sm"
            onClick={() => addPhone.mutate({ phone: phoneInput.trim(), isPrimary: phonePrimary })}
            loading={addPhone.isPending}
            disabled={!phoneInput.trim()}
            className={btnCls}
          >
            Add
          </Button>
          <label className="flex items-center gap-1 text-[10px] text-secondary cursor-pointer select-none flex-shrink-0">
            <input
              type="checkbox"
              checked={phonePrimary}
              onChange={(e) => setPhonePrimary(e.target.checked)}
              className="w-3 h-3 rounded"
            />
            Primary
          </label>
        </div>
        {phones && phones.length > 0 && (
          <div className="space-y-0.5 mt-1">
            {phones.map((p) => (
              <div key={p.id} className="flex items-center gap-1.5 text-xs">
                <Phone className="w-3 h-3 text-secondary flex-shrink-0" />
                <span className="font-mono text-primary-text">{p.phone}</span>
                {p.isPrimary && <span className="text-[10px] bg-primary/10 text-primary px-1 rounded-full font-medium">Primary</span>}
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-secondary italic mt-0.5">
          Adds to this unit's phone list.
        </p>
      </div>

      {/* Row 4: Comment (full width) */}
      <div>
        <label className={labelCls}>Comment</label>
        <div className="flex gap-1.5">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a comment..."
            rows={1}
            className={cn(inputCls, 'resize-none py-2 leading-tight')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && noteText.trim()) {
                e.preventDefault();
                addComment.mutate(noteText.trim());
              }
            }}
          />
          <Button
            size="sm"
            onClick={() => addComment.mutate(noteText.trim())}
            loading={addComment.isPending}
            disabled={!noteText.trim()}
            className={btnCls}
          >
            Send
          </Button>
        </div>
        {displayComments.length > 0 && (
          <div className="space-y-1 mt-1.5">
            {displayComments.map((c) => (
              <div key={c.id} className="bg-[#f9fafb] rounded-md px-2 py-1">
                <p className="text-[11px] text-primary-text leading-snug">{c.body}</p>
                <p className="text-[10px] text-secondary mt-0.5">
                  {c.authorName} · {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                </p>
              </div>
            ))}
            {(comments?.length ?? 0) > 3 && (
              <button
                onClick={() => setShowAllComments((v) => !v)}
                className="text-[10px] text-primary hover:underline font-medium"
              >
                {showAllComments
                  ? 'Show less'
                  : `Show all (${comments!.length})`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reminder helper — Slice D */}
      <p className="text-[10px] text-secondary italic">
        Reminder: use the bell icon in the top nav.
      </p>

    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function LandlordDrawer({ landlordId, onClose, onJumpToUnit }: LandlordDrawerProps) {
  const { hasRole, user } = useAuth();
  const { toast } = useToast();
  const { logActivity, logAndCall, logAndWhatsApp } = useActivityLog();
  const queryClient = useQueryClient();
  // Sherzod 2026-05-03: only user (agent) and super_admin (super-agent)
  // can delete a phone. manager/admin see no X button on phones.
  const canDeletePhone = user?.role === 'user' || user?.role === 'super_admin';

  const [showAllActivity, setShowAllActivity] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [editingStage, setEditingStage] = useState(false);
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [pendingLogType, setPendingLogType] = useState<string | null>(null);
  const [justLoggedType, setJustLoggedType] = useState<string | null>(null);
  const [expandedUnits, setExpandedUnits] = useState<Record<number, boolean>>({});
  const [expandedAreas, setExpandedAreas] = useState<Record<string, boolean>>({});
  const [expandedBuildings, setExpandedBuildings] = useState<Record<string, boolean>>({});

  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [pendingAssignAgentId, setPendingAssignAgentId] = useState<number>(0);

  // Collapsible profile block — persisted to localStorage
  const PROFILE_LS_KEY = 'imza:drawer:profile:collapsed';
  const [profileCollapsed, setProfileCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(PROFILE_LS_KEY) === 'true'; } catch { return false; }
  });
  function toggleProfileCollapsed() {
    setProfileCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(PROFILE_LS_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }

  const [conflictInfo, setConflictInfo] = useState<{
    agentId: number;
    agentName: string;
    assignedAt: string;
    pendingAgentId: number;
  } | null>(null);

  const isManager = hasRole('manager');

  // ── Data ──────────────────────────────────────────────────────────────────

  const { data: landlord, isLoading } = useQuery({
    queryKey: ['landlord', landlordId],
    queryFn: () => landlordsApi.get(landlordId!),
    enabled: landlordId !== null,
  });

  const { data: portfolio, isLoading: portfolioLoading } = useQuery({
    queryKey: ['landlord-portfolio', landlordId],
    queryFn: () => landlordsApi.getPortfolio(landlordId!),
    enabled: landlordId !== null,
    staleTime: 60_000,
  });

  const { data: availableTags } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
    enabled: showTagPopover,
  });

  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agentsApi.list(),
    enabled: isManager && showAssignDropdown,
  });
  const agents = agentsData?.items ?? [];

  // ── Mutations ─────────────────────────────────────────────────────────────

  const patchLandlord = useMutation({
    mutationFn: (body: { stage?: StageValue; notes?: string; needsReview?: boolean; name?: string; building?: string }) =>
      landlordsApi.patch(landlordId!, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      queryClient.invalidateQueries({ queryKey: ['landlords'] });
      if (variables.stage) {
        const sl = STAGE_OPTIONS.find((o) => o.value === variables.stage)?.label ?? variables.stage;
        toast(`Stage updated to ${sl}`, 'success');
      } else if (variables.name) {
        toast('Name saved', 'success');
      } else if (variables.building) {
        toast('Building saved', 'success');
      } else if (variables.needsReview !== undefined) {
        toast(variables.needsReview ? 'Flagged for review' : 'Review flag cleared', 'success');
      } else {
        toast('Saved', 'success');
      }
      setEditingStage(false);
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const starMutation = useMutation({
    mutationFn: () => landlordsApi.patch(landlordId!, { isStarred: !(landlord as any)?.isStarred } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      queryClient.invalidateQueries({ queryKey: ['landlords'] });
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: () =>
      activityApi.log({ landlord_id: landlordId!, type: 'note_added', payload: { note: noteText } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      setNoteText('');
      toast('Note saved', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const attachTag = useMutation({
    mutationFn: (tag_id: number) => landlordsApi.addTag(landlordId!, tag_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      setShowTagPopover(false);
      setNewTagName('');
      toast('Tag added', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const detachTag = useMutation({
    mutationFn: (tagId: number) => landlordsApi.removeTag(landlordId!, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      toast('Tag removed', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const removePhone = useMutation({
    mutationFn: (phoneId: number) => landlordsApi.removePhone(landlordId!, phoneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      queryClient.invalidateQueries({ queryKey: ['landlord-portfolio', landlordId] });
      toast('Phone removed', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const createAndAttachTag = useMutation({
    mutationFn: async (name: string) => {
      const created = await tagsApi.create(name);
      return landlordsApi.addTag(landlordId!, created.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setShowTagPopover(false);
      setNewTagName('');
      toast('Tag created', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const assignMutation = useMutation({
    mutationFn: ({ agentId, force }: { agentId: number; force: boolean }) =>
      assignmentsApi.create({ landlord_id: landlordId!, agent_id: agentId }, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['landlord', landlordId] });
      queryClient.invalidateQueries({ queryKey: ['landlords'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      setShowAssignDropdown(false);
      setConflictInfo(null);
      toast('Agent assigned', 'success');
    },
    onError: (err: any) => {
      if (err?.statusCode === 409 && err?.body?.existingAssignment) {
        setConflictInfo({
          agentId: err.body.existingAssignment.agentId ?? 0,
          agentName: err.body.existingAssignment.agentName ?? 'Unknown',
          assignedAt: err.body.existingAssignment.assignedAt,
          pendingAgentId: pendingAssignAgentId,
        });
      } else {
        toast(err.message ?? 'Assignment failed', 'error');
      }
    },
  });


  // ── Helpers ───────────────────────────────────────────────────────────────

  const recentActivity = landlord?.recentActivity ?? [];
  const displayActivity = showAllActivity ? recentActivity : recentActivity.slice(0, 5);

  const displayName =
    !landlord?.name || landlord.name === '#N/A' ? null : landlord.name;

  // Use portfolio phones/emails (deduplicated across all rows), fallback to single-row data
  const allPhones = portfolio?.phones ?? landlord?.phones ?? [];
  const allEmails = portfolio?.emails ?? landlord?.emails ?? [];
  const primaryPhone = allPhones.find((p) => p.isPrimary) ?? allPhones[0];
  const primaryEmail = allEmails.find((e) => e.isPrimary) ?? allEmails[0];

  const isMultiProperty = (portfolio?.landlordIds?.length ?? 0) > 1;

  function toggleUnit(id: number) {
    setExpandedUnits((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleArea(key: string) {
    setExpandedAreas((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleBuilding(key: string) {
    setExpandedBuildings((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Drawer
      open={landlordId !== null}
      onClose={onClose}
      title={displayName ?? 'Landlord details'}
    >
      {isLoading || !landlord ? (
        <div className="p-5 space-y-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <div className="flex flex-col bg-[#f5f6f8]">

          {/* ── COLLAPSIBLE PROFILE STRIP ─────────────────────── */}
          {/* Strip is always visible; content below it collapses */}
          <div className="bg-white border-b border-border">
            <button
              onClick={toggleProfileCollapsed}
              className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#f9fafb] transition-colors text-left"
              aria-expanded={!profileCollapsed}
              aria-label={profileCollapsed ? 'Expand profile' : 'Collapse profile'}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="font-semibold text-primary-text text-sm truncate">
                    {displayName ?? 'Landlord details'}
                  </p>
                  {/* "Owns N units" badge — shows portfolio size at a glance,
                      visible even when the profile block is collapsed. */}
                  {(() => {
                    const total = portfolio?.totalUnits ?? landlord?.units.length ?? 0;
                    if (total <= 0) return null;
                    return (
                      <span
                        className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full"
                        title={`Owns ${total} unit${total === 1 ? '' : 's'}`}
                      >
                        <Building2 className="w-2.5 h-2.5" />
                        {total} unit{total === 1 ? '' : 's'}
                      </span>
                    );
                  })()}
                </div>
                {primaryPhone && (
                  <p className="text-xs font-mono text-secondary mt-0.5 truncate">{primaryPhone.phone}</p>
                )}
              </div>
              <ChevronRight className={cn(
                'w-4 h-4 text-secondary flex-shrink-0 transition-transform duration-200',
                !profileCollapsed && 'rotate-90'
              )} />
            </button>
          </div>

          {/* ── COLLAPSIBLE CONTENT: header + contact + log + timeline ── */}
          <div className={cn(
            'overflow-hidden transition-all duration-200',
            profileCollapsed ? 'max-h-0' : 'max-h-[9999px]'
          )}>

          {/* ── HEADER INFO BAR ──────────────────────────────── */}
          <div className="bg-white px-5 py-4 border-b border-border">
            {/* Mobile + Email summary line */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-primary-text">
              {primaryPhone && (
                <span className="flex items-center gap-1.5">
                  <span className="text-secondary">Mobile:</span>
                  <Phone className="w-3.5 h-3.5 text-secondary" />
                  <span className="font-mono font-medium">{primaryPhone.phone}</span>
                </span>
              )}
              {primaryEmail ? (
                <span className="flex items-center gap-1.5">
                  <span className="text-secondary">Email:</span>
                  <a href={`mailto:${primaryEmail.email}`} className="text-primary hover:underline">{primaryEmail.email}</a>
                </span>
              ) : (
                <span className="text-secondary">Email: N/A</span>
              )}
            </div>

            {/* Stats line — portfolio-aware */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-secondary mt-1.5">
              {portfolio ? (
                <>
                  <span><span className="font-semibold text-primary-text">{portfolio.totalUnits}</span> units</span>
                  <span><span className="font-semibold text-primary-text">{portfolio.totalBuildings}</span> buildings</span>
                  <span><span className="font-semibold text-primary-text">{portfolio.totalAreas}</span> areas</span>
                  {isMultiProperty && (
                    <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-semibold">
                      Portfolio
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span>Units: {landlord.units.length}</span>
                  {landlord.areaName && <span>Area: {landlord.areaName}</span>}
                  {landlord.building && <span>Building: {landlord.building}</span>}
                </>
              )}
            </div>

            {/* Stage + review badge + action buttons */}
            <div className="flex items-center gap-2 mt-3">
              {/* Stage badge */}
              {isManager && editingStage ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {STAGE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => patchLandlord.mutate({ stage: opt.value })}
                      disabled={patchLandlord.isPending}
                      className={cn(
                        'text-xs px-2.5 py-1 rounded-full font-semibold border transition-all',
                        opt.value === landlord.stage
                          ? stageClasses(opt.value) + ' ring-2 ring-offset-1 ring-current'
                          : 'border-border bg-white text-secondary hover:border-primary hover:text-primary'
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                  <button onClick={() => setEditingStage(false)} className="text-xs text-secondary hover:text-primary-text px-1">
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => isManager && setEditingStage(true)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all',
                    stageClasses(landlord.stage),
                    isManager && 'hover:opacity-80 cursor-pointer active:scale-95'
                  )}
                >
                  <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', stageDotClass(landlord.stage))} />
                  {stageLabel(landlord.stage)}
                  {isManager && <ChevronDown className="w-3 h-3 opacity-60" />}
                </button>
              )}

              {landlord.needsReview && (
                <span className="flex items-center gap-1 text-[10px] font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200 px-1.5 py-0.5 rounded-full">
                  <AlertTriangle className="w-3 h-3" />
                  Review
                </span>
              )}

              {/* Call + WhatsApp action buttons */}
              <div className="ml-auto flex items-center gap-1.5">
                <button
                  onClick={() => starMutation.mutate()}
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
                    (landlord as any).isStarred
                      ? 'bg-yellow-50 text-yellow-400 hover:text-yellow-500'
                      : 'bg-gray-100 text-gray-300 hover:text-yellow-400 hover:bg-yellow-50'
                  )}
                  aria-label={(landlord as any).isStarred ? 'Unstar' : 'Star as distress deal'}
                >
                  <Star className="w-3.5 h-3.5" fill={(landlord as any).isStarred ? 'currentColor' : 'none'} />
                </button>
                {primaryPhone && !primaryPhone.phone.includes('*') && (
                  <>
                    <a
                      href={`tel:${primaryPhone.phone}`}
                      onClick={(e) => { e.preventDefault(); logAndCall(landlord.id, primaryPhone.phone); }}
                      className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-white hover:bg-blue-600 transition-colors"
                      aria-label="Call"
                    >
                      <Phone className="w-3.5 h-3.5" />
                    </a>
                    <a
                      href={`https://wa.me/${primaryPhone.phone.replace(/^\+/, '').replace(/\s/g, '')}`}
                      onClick={(e) => { e.preventDefault(); logAndWhatsApp(landlord.id, primaryPhone.phone); }}
                      className="w-8 h-8 rounded-lg bg-[#25D366] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                      aria-label="WhatsApp"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <WhatsAppIcon className="w-3.5 h-3.5" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── CONTACT & DETAILS CARD ──────────────────────── */}
          <div className="px-4 pt-3">
            <div className="bg-white rounded-xl border border-border shadow-sm p-4 space-y-3">
              {/* Contact info */}
              {allPhones.length > 0 && (
                <div>
                  <SectionLabel>Phones</SectionLabel>
                  <div className="mt-1 space-y-1">
                    {allPhones.map((p) => (
                      <div key={p.id} className="flex items-center gap-2 text-sm">
                        <Phone className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                        <span className="font-mono text-primary-text">{p.phone}</span>
                        {p.isPrimary && <span className="text-[10px] bg-primary/10 text-primary px-1.5 rounded-full font-medium">Primary</span>}
                        {p.isMobile && <span className="text-[10px] bg-hover-bg text-secondary px-1.5 rounded-full">Mobile</span>}
                        {!p.phone.includes('*') && <CopyButton text={p.phone} />}
                        {canDeletePhone && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Remove phone ${p.phone}?`)) {
                                removePhone.mutate(p.id);
                              }
                            }}
                            disabled={removePhone.isPending}
                            className="ml-auto p-1 rounded hover:bg-danger/10 text-secondary hover:text-danger transition-colors disabled:opacity-50"
                            aria-label={`Remove phone ${p.phone}`}
                            title="Remove phone (agent / super-agent only)"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {allEmails.length > 0 && (
                <div>
                  <SectionLabel>Emails</SectionLabel>
                  <div className="mt-1 space-y-1">
                    {allEmails.map((e) => (
                      <div key={e.id} className="flex items-center gap-2 text-sm">
                        <Mail className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                        <a href={`mailto:${e.email}`} className="text-primary hover:underline truncate">{e.email}</a>
                        {e.isPrimary && <span className="text-[10px] bg-primary/10 text-primary px-1.5 rounded-full font-medium">Primary</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              <div>
                <SectionLabel>Tags</SectionLabel>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  {landlord.tags.length > 0 ? (
                    landlord.tags.map((tag) => (
                      <span key={tag.id} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                        {tag.name}
                        {isManager && (
                          <button onClick={() => detachTag.mutate(tag.id)} className="text-primary/60 hover:text-primary" aria-label={`Remove ${tag.name}`}>
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-secondary">No tags</span>
                  )}
                  {isManager && (
                    <button
                      onClick={() => setShowTagPopover((v) => !v)}
                      className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline font-medium px-2 py-0.5 rounded-full bg-primary/5 border border-primary/10"
                    >
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  )}
                </div>
              </div>

              {/* Assignee */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <SectionLabel>Assignee</SectionLabel>
                  {isManager ? (
                    <div className="relative mt-1">
                      <button
                        onClick={() => setShowAssignDropdown((v) => !v)}
                        className="w-full px-3 py-2 bg-[#f9fafb] border border-border rounded-lg text-sm text-primary-text flex items-center justify-between gap-1.5 hover:border-primary transition-colors"
                      >
                        <span className="flex items-center gap-1.5 truncate">
                          <User className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                          {landlord.assignedAgentName || 'Unassigned'}
                        </span>
                        <ChevronDown className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                      </button>
                      {showAssignDropdown && (
                        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto py-1">
                          {landlord.assignedAgentId && (
                            <button
                              onClick={() => {
                                toast('Use Admin panel to remove assignments', 'info');
                                setShowAssignDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-danger hover:bg-red-50 transition-colors"
                            >
                              Remove assignment
                            </button>
                          )}
                          {agents.length > 0 ? agents.map((agent) => (
                            <button
                              key={agent.id}
                              onClick={() => {
                                setPendingAssignAgentId(agent.id);
                                assignMutation.mutate({ agentId: agent.id, force: false });
                              }}
                              disabled={agent.id === landlord.assignedAgentId}
                              className={cn(
                                'w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2',
                                agent.id === landlord.assignedAgentId
                                  ? 'text-primary bg-primary/5 font-medium'
                                  : 'text-primary-text hover:bg-hover-bg'
                              )}
                            >
                              <User className="w-3.5 h-3.5 text-secondary" />
                              {agent.name}
                              {agent.id === landlord.assignedAgentId && (
                                <Check className="w-3.5 h-3.5 text-primary ml-auto" />
                              )}
                            </button>
                          )) : (
                            <p className="px-3 py-2 text-sm text-secondary">No agents available</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1 px-3 py-2 bg-[#f9fafb] border border-border rounded-lg text-sm text-primary-text flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-secondary" />
                      {landlord.assignedAgentName || 'Unassigned'}
                    </div>
                  )}
                </div>
                <div>
                  <SectionLabel>Follow-up</SectionLabel>
                  <div className="mt-1 px-3 py-2 bg-[#f9fafb] border border-border rounded-lg text-sm text-secondary">
                    —
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center gap-1">
                  <SectionLabel>Notes</SectionLabel>
                </div>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a note..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && noteText.trim()) addNoteMutation.mutate();
                    }}
                    className="flex-1 px-3 py-2 bg-[#f9fafb] border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <Button
                    size="sm"
                    onClick={() => noteText.trim() && addNoteMutation.mutate()}
                    loading={addNoteMutation.isPending}
                    disabled={!noteText.trim()}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* ── TAG POPOVER (shared across cards) ─────────────── */}
          {showTagPopover && isManager && (
            <div className="mx-4 mt-3 bg-white border border-border rounded-xl p-3 shadow-lg">
              <div className="flex gap-2 mb-2">
                <input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="New tag name..."
                  className="flex-1 text-sm border border-border rounded-lg px-3 h-9 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTagName.trim()) createAndAttachTag.mutate(newTagName.trim());
                  }}
                />
                <Button size="sm" onClick={() => newTagName.trim() && createAndAttachTag.mutate(newTagName.trim())} loading={createAndAttachTag.isPending} disabled={!newTagName.trim()}>
                  Create
                </Button>
              </div>
              {availableTags && availableTags.items.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {availableTags.items
                    .filter((t) => !landlord.tags.some((lt) => lt.id === t.id))
                    .map((tag) => (
                      <button key={tag.id} onClick={() => attachTag.mutate(tag.id)} className="text-xs px-2.5 py-1 rounded-full bg-hover-bg border border-border text-secondary hover:border-primary hover:text-primary transition-colors">
                        {tag.name}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* ── QUICK LOG ─────────────────────────────────────── */}
          {/* Sherzod 2026-05-03: three buttons act on the lead.
              - Optimistic insert into landlord.recentActivity, reconciled
                on settle. On error, the optimistic row is rolled back.
              - WhatsApp also opens wa.me (target=_blank) using a valid
                UAE mobile (+9715…), disabled if none available.
              - "Logged ✓" flashes on the just-clicked button for ~1.5s. */}
          <div className="px-4 pt-4">
            <div className="bg-white rounded-xl border border-border shadow-sm p-4">
              <h3 className="text-[11px] font-bold text-primary uppercase tracking-wider mb-3">Log Activity</h3>
              <div className="grid grid-cols-3 gap-2">
                {(() => {
                  const landlordKey = ['landlord', landlordId];

                  // Pick a valid UAE mobile (+9715X). Fallback: any phone
                  // marked is_mobile or starts with "+9715". Disabled if none.
                  const mobilePhones = allPhones.filter(
                    (p) => /^\+?9715/.test(p.phone.replace(/\s/g, '')) || p.isMobile
                  );
                  const waPhone = (mobilePhones.find((p) => p.isPrimary) ?? mobilePhones[0])?.phone ?? null;
                  const callPhone = primaryPhone?.phone ?? null;

                  async function fireLog(
                    apiType: 'call_answered' | 'call_no_answer' | 'whatsapp_sent',
                    phone: string | null
                  ) {
                    setPendingLogType(apiType);
                    // Optimistic prepend: synthetic id (negative so it can't
                    // collide with a real serial id). Reconciled on settle.
                    const optimisticId = -Date.now();
                    const optimisticEvent = {
                      id: optimisticId,
                      userId: user?.id ?? null,
                      userName: user?.name ?? 'You',
                      type: apiType.startsWith('call') ? 'call' : 'whatsapp',
                      detail: { api_type: apiType, ...(phone ? { phone } : {}) },
                      createdAt: new Date().toISOString(),
                    };
                    const previous = queryClient.getQueryData<any>(landlordKey);
                    if (previous) {
                      queryClient.setQueryData(landlordKey, {
                        ...previous,
                        recentActivity: [optimisticEvent, ...(previous.recentActivity ?? [])],
                      });
                    }
                    try {
                      await activityApi.log({
                        landlord_id: landlordId!,
                        type: apiType,
                        payload: phone ? { phone } : undefined,
                      });
                      // Reconcile from server
                      queryClient.invalidateQueries({ queryKey: landlordKey });
                      setPendingLogType(null);
                      setJustLoggedType(apiType);
                      window.setTimeout(() => setJustLoggedType((v) => (v === apiType ? null : v)), 1500);
                    } catch (err) {
                      // Roll back optimistic row
                      if (previous) queryClient.setQueryData(landlordKey, previous);
                      setPendingLogType(null);
                      toast(err instanceof Error ? err.message : 'Failed to log activity', 'error');
                    }
                  }

                  return (
                    <>
                      <button
                        disabled={pendingLogType !== null}
                        onClick={() => fireLog('call_answered', callPhone)}
                        className="flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-lg bg-success text-white text-[11px] font-semibold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-60"
                      >
                        {pendingLogType === 'call_answered'
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : justLoggedType === 'call_answered'
                            ? <Check className="w-4 h-4" />
                            : <PhoneCall className="w-4 h-4" />}
                        <span>{justLoggedType === 'call_answered' ? 'Logged ✓' : 'Answered'}</span>
                      </button>

                      <button
                        disabled={pendingLogType !== null}
                        onClick={() => fireLog('call_no_answer', callPhone)}
                        className="flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-lg bg-danger text-white text-[11px] font-semibold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-60"
                      >
                        {pendingLogType === 'call_no_answer'
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : justLoggedType === 'call_no_answer'
                            ? <Check className="w-4 h-4" />
                            : <PhoneOff className="w-4 h-4" />}
                        <span>{justLoggedType === 'call_no_answer' ? 'Logged ✓' : 'No answer'}</span>
                      </button>

                      <button
                        disabled={pendingLogType !== null || !waPhone}
                        title={waPhone ? 'Open WhatsApp + log activity' : 'No mobile number available'}
                        onClick={() => {
                          if (!waPhone) return;
                          // Open WhatsApp first so the click stays in the user-gesture
                          // window (Safari/Firefox block window.open from async paths).
                          const normalized = waPhone.replace(/^\+/, '').replace(/\s/g, '');
                          window.open(`https://wa.me/${normalized}`, '_blank', 'noopener,noreferrer');
                          fireLog('whatsapp_sent', waPhone);
                        }}
                        className="flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-lg bg-[#25D366] text-white text-[11px] font-semibold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {pendingLogType === 'whatsapp_sent'
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : justLoggedType === 'whatsapp_sent'
                            ? <Check className="w-4 h-4" />
                            : <WhatsAppIcon className="w-4 h-4" />}
                        <span>{justLoggedType === 'whatsapp_sent' ? 'Logged ✓' : 'WhatsApp'}</span>
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* ── FULL ACTIVITY TIMELINE ─────────────────────────── */}
          {recentActivity.length > 0 && (
            <div className="px-4 pt-3">
              <div className="bg-white rounded-xl border border-border shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[11px] font-bold text-primary uppercase tracking-wider">Activity Timeline</h3>
                  <span className="text-[11px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-semibold">{recentActivity.length}</span>
                </div>
                <div className="relative">
                  <div className="absolute left-[5px] top-2 bottom-2 w-px bg-border" aria-hidden="true" />
                  <div className="space-y-3 pl-5">
                    {displayActivity.map((ev, idx) => {
                      const cfg = ACTIVITY_CONFIG[ev.type] ?? { dot: 'bg-gray-300' };
                      const detail = (ev.detail ?? {}) as Record<string, unknown>;
                      const evApiType =
                        typeof detail.api_type === 'string' ? detail.api_type
                        : typeof detail.apiType === 'string' ? detail.apiType
                        : null;
                      // Red dot for no-answer (Sherzod 2026-05-03 legend).
                      const dotClass =
                        evApiType === 'call_no_answer' ? 'bg-danger' : cfg.dot;
                      const phrase = activityActionPhrase({
                        type: ev.type,
                        detail: ev.detail as Record<string, unknown> | null,
                      });
                      const userName = ev.userName ?? 'Someone';
                      const note = ev.detail && typeof (ev.detail as Record<string, unknown>).note === 'string'
                        ? String((ev.detail as Record<string, unknown>).note)
                        : null;
                      return (
                        <div key={ev.id} className="relative animate-activity-appear" style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'both' }}>
                          <div className={cn('absolute -left-5 top-1.5 w-3 h-3 rounded-full border-2 border-white', dotClass)} aria-hidden="true" />
                          <div>
                            <p className="text-sm text-primary-text leading-snug">
                              <span className="font-bold">{userName}</span>{' '}{phrase}
                            </p>
                            <p className="text-[11px] text-secondary mt-0.5">
                              {formatDistanceToNow(new Date(ev.createdAt), { addSuffix: true })}
                            </p>
                            {note && <p className="text-xs text-primary-text mt-1 bg-[#f9fafb] rounded-lg px-2.5 py-1.5 leading-relaxed">{note}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {recentActivity.length > 5 && (
                  <button onClick={() => setShowAllActivity((v) => !v)} className="flex items-center gap-1 text-xs text-primary hover:underline mt-3 font-medium">
                    {showAllActivity ? <><ChevronUp className="w-3.5 h-3.5" /> Show less</> : <><ChevronDown className="w-3.5 h-3.5" /> Show all {recentActivity.length}</>}
                  </button>
                )}
              </div>
            </div>
          )}

          </div>{/* end collapsible profile content */}

          {/* ── PORTFOLIO PROPERTIES ──────────────────────────── */}
          {/* Sherzod 2026-05-03: portfolio moved BELOW the update blocks
              (contact/log/timeline). Agents update the lead first, then
              scan the portfolio — not the other way around. */}
          {portfolio ? (
            <div className="px-4 pt-3 space-y-1">
              {portfolio.areas
                .flatMap((area) =>
                  area.buildings.flatMap((building) =>
                    building.units.map((unit) => ({
                      unit,
                      buildingName: building.building || 'Unknown',
                      stage: unit.stage,
                      landlordId: building.landlordId,
                      // Sherzod 2026-05-06: name-group source phone chip.
                      // Helps tell apart same-name profiles by phone.
                      sourcePrimaryPhone: building.sourcePrimaryPhone,
                    }))
                  )
                )
                .sort((a, b) => {
                  const bn = (a.buildingName || '').localeCompare(b.buildingName || '');
                  if (bn !== 0) return bn;
                  // Smart-numeric on unit_no — treats "BA-R-99" < "BA-R-100"
                  const an = parseInt(String(a.unit.unitNo ?? '').replace(/\D/g, ''), 10) || 0;
                  const bnum = parseInt(String(b.unit.unitNo ?? '').replace(/\D/g, ''), 10) || 0;
                  if (an !== bnum) return an - bnum;
                  return String(a.unit.unitNo ?? '').localeCompare(String(b.unit.unitNo ?? ''));
                })
                .map(({ unit, buildingName, stage, landlordId, sourcePrimaryPhone }) => {
                  const cardKey = `unit-${unit.id}`;
                  const isExpanded = expandedBuildings[cardKey] ?? false;
                  return (
                    <div
                      key={cardKey}
                      className="bg-white rounded-lg border border-border shadow-sm overflow-hidden animate-fade-in"
                    >
                      <button
                        onClick={() => toggleBuilding(cardKey)}
                        className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-[#f9fafb] transition-colors text-left"
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Building2 className="w-3 h-3 text-primary flex-shrink-0" />
                          <span className="text-xs font-semibold text-primary-text truncate">
                            {buildingName}
                          </span>
                          {unit.unitNo && (
                            <button
                              type="button"
                              data-unit-id={unit.id}
                              title={onJumpToUnit ? 'Open this unit in the main table' : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onJumpToUnit) onJumpToUnit(unit.id);
                              }}
                              className={cn(
                                'text-xs font-mono text-secondary flex-shrink-0 inline-flex items-center gap-0.5',
                                onJumpToUnit
                                  ? 'cursor-pointer hover:text-primary hover:underline'
                                  : 'cursor-default'
                              )}
                            >
                              · {unit.unitNo}
                              {onJumpToUnit && (
                                <ChevronRight className="w-2.5 h-2.5 opacity-60" />
                              )}
                            </button>
                          )}
                          {/* Source-profile chip: phone of the landlord row this
                              unit belongs to. Visible only when the name-group
                              has multiple sibling profiles AND a phone exists,
                              so the chip differentiates rather than clutters. */}
                          {portfolio && (portfolio.landlordIds?.length ?? 1) > 1 && sourcePrimaryPhone && (
                            <span
                              className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono text-secondary bg-hover-bg px-1.5 py-0.5 rounded-full flex-shrink-0"
                              title={`Source profile: landlord #${landlordId} · ${sourcePrimaryPhone}`}
                            >
                              <Phone className="w-2.5 h-2.5" />
                              {sourcePrimaryPhone}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className={cn(
                            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold',
                            stageClasses(stage)
                          )}>
                            <span className={cn('w-1 h-1 rounded-full', stageDotClass(stage))} />
                            {stageLabel(stage)}
                          </span>
                          {isExpanded
                            ? <ChevronUp className="w-3.5 h-3.5 text-secondary" />
                            : <ChevronDown className="w-3.5 h-3.5 text-secondary" />
                          }
                        </div>
                      </button>

                      {/* Expanded card body */}
                      {isExpanded && (
                        <div className="border-t border-border/50 px-3 py-2 space-y-1.5 bg-[#fafbfc]">
                          {/* Beds + View inline (Sherzod 2026-05-03 compact layout) */}
                          <div className="flex items-center gap-3 text-[11px] text-secondary">
                            <span><span className="font-semibold text-primary-text">{unit.beds || '—'}</span> bed</span>
                            <span className="text-border">·</span>
                            <span className="truncate"><span className="font-semibold text-primary-text">{unit.view || '—'}</span> view</span>
                          </div>

                          <UnitCardBody
                            unitId={unit.id}
                            unitNo={unit.unitNo}
                            landlordId={landlordId}
                            portfolioQueryKey={['landlord-portfolio', landlordId]}
                            isManager={isManager}
                            initialStage={stage}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : portfolioLoading ? (
            <div className="px-4 pt-3 space-y-2">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : landlord.units.length > 0 && (
            /* Fallback: single-row units if portfolio hasn't loaded */
            <div className="px-4 pt-3 space-y-1">
              {landlord.units.map((unit, idx) => (
                <div key={unit.id} className="bg-white rounded-lg border border-border shadow-sm px-3 py-1.5 flex items-center gap-2 text-xs">
                  <span className="font-medium text-primary-text">{unit.unitNo || `Unit ${idx + 1}`}</span>
                  {unit.beds && <span className="text-secondary">{unit.beds} bed</span>}
                  {unit.view && <span className="text-secondary ml-auto truncate">{unit.view}</span>}
                </div>
              ))}
            </div>
          )}

          {/* ── MANAGER CONTROLS ──────────────────────────────── */}
          {isManager && (
            <div className="px-4 pt-3">
              <Button size="sm" variant={landlord.needsReview ? 'danger' : 'secondary'} onClick={() => patchLandlord.mutate({ needsReview: !landlord.needsReview })} loading={patchLandlord.isPending} className="w-full">
                <AlertTriangle className="w-3.5 h-3.5" />
                {landlord.needsReview ? 'Clear review flag' : 'Flag for review'}
              </Button>
            </div>
          )}

          <div className="h-6" aria-hidden="true" />
        </div>
      )}
      {/* Conflict-of-interest dialog */}
      {conflictInfo && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
          onClick={() => setConflictInfo(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-5 max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-primary-text mb-2">Assignment Conflict</h3>
            <p className="text-sm text-primary-text mb-1">
              This landlord is already assigned to <strong>{conflictInfo.agentName}</strong>.
            </p>
            <p className="text-xs text-secondary mb-4">
              Assigned on {new Date(conflictInfo.assignedAt).toLocaleDateString()}. Override and reassign?
            </p>
            <div className="flex gap-2">
              <Button
                variant="danger"
                size="sm"
                className="flex-1"
                loading={assignMutation.isPending}
                onClick={() => assignMutation.mutate({ agentId: conflictInfo.pendingAgentId, force: true })}
              >
                Override
              </Button>
              <Button variant="secondary" size="sm" className="flex-1" onClick={() => setConflictInfo(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
